import { AxiosError } from 'axios'
import FormValidator, { IModelError, IRuleOption, IPartialError } from './validator'

type IAnyObject = { [key: string]: any }

/**
 * deepMerge: merge objects deeply, later sources always override earlier values for the same key.
 *
 * @example
 * // Primitive override — source wins
 * deepMerge({ a: 1, b: true }, { b: false })
 * // → { a: 1, b: false }
 *
 * // null override — null wins over nested object
 * deepMerge({ a: { x: 1, y: 2 } }, { a: null })
 * // → { a: null }
 *
 * // undefined override — undefined wins
 * deepMerge({ a: 'hello' }, { a: undefined })
 * // → { a: undefined }
 *
 * // Nested deep merge — both sides are plain objects → recurse
 * deepMerge({ a: { x: 1, y: 2 } }, { a: { x: 10, z: 3 } })
 * // → { a: { x: 10, y: 2, z: 3 } }
 *
 * // Multiple sources — last source wins on conflict
 * deepMerge({ a: 1 }, { a: 2 }, { a: 3 })
 * // → { a: 3 }
 */
const deepMerge = <T extends IAnyObject>(model: T, ...sources: (Partial<T> | undefined)[]): T => {
  const validSources = sources.filter((x): x is Partial<T> => !!x)
  if (validSources.length < 1) {
    return model
  }

  const target = Object.assign({}, model) as T

  for (const source of validSources) {
    for (const key of Object.keys(source)) {
      const srcVal = (source as any)[key]
      const tgtVal = (target as any)[key]
      if (
        srcVal !== null &&
        typeof srcVal === 'object' &&
        !Array.isArray(srcVal) &&
        tgtVal !== null &&
        typeof tgtVal === 'object' &&
        !Array.isArray(tgtVal)
      ) {
        // Both are plain objects → deep merge recursively
        ;(target as any)[key] = deepMerge(tgtVal, srcVal)
      } else {
        // Source value always wins (null, false, undefined, array, primitive)
        ;(target as any)[key] = srcVal
      }
    }
  }

  return target
}

export const getErrorMessage = function <TModel>(MessageErrors: IPartialError<TModel> | undefined, key?: keyof TModel): IModelError {
  return MessageErrors && (MessageErrors as any)[key] ? { ...(MessageErrors as any)[key][0], error: true } : { error: false, message: '' }
}

type IModelBase = {
  [key: string]: any
}

export const convertFormDataToJson = function <TModel>(form: FormData): Partial<TModel> {
  const data = Array.from(form as any).reduce<Partial<TModel>>((a: unknown | any, b: any) => {
    if (!a[b[0]]) {
      a[b[0]] = b[1]
    } else if (Array.isArray(a[b[0]])) {
      a[b[0]].push(b[1])
    } else {
      a[b[0]] = [a[b[0]], b[1]]
    }
    return a
  }, {} as any) as any

  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (typeof value === 'string' && (value.toString().toLocaleLowerCase() === 'true' || value.toString().toLocaleLowerCase() === 'false')) {
      data[key] = value.toString().toLocaleLowerCase() === 'true'
    }
  })
  return data
}

export const trimAllStrings = <T>(obj: T): Partial<T> => {
  if (typeof obj === 'string') {
    // Don't trim color values (hex colors start with #)
    if (obj.startsWith('#') && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(obj)) {
      return obj as any
    }
    return obj.trim() as any
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => trimAllStrings(item)) as any
  }

  // Skip File objects to preserve file data
  if (obj instanceof File) {
    return obj as any
  }

  // Skip FileList objects (multiple file inputs)
  if (obj instanceof FileList) {
    return obj as any
  }

  // Skip Date objects to preserve date data
  if (obj instanceof Date) {
    return obj as any
  }

  // Skip numeric values, booleans, null, undefined
  if (typeof obj === 'number' || typeof obj === 'boolean' || obj === null || obj === undefined) {
    return obj as any
  }

  // Skip DOM elements
  if (obj instanceof HTMLElement) {
    return obj as any
  }

  // Skip functions
  if (typeof obj === 'function') {
    return obj as any
  }

  if (obj && typeof obj === 'object') {
    const result: any = {}
    Object.keys(obj).forEach((key) => {
      result[key] = trimAllStrings((obj as any)[key])
    })
    return result
  }
  return obj
}

export const singleValidate = function <TModel, TPartial = TModel>(
  key: keyof TModel,
  modelState: TPartial,
  MessageErrors: IPartialError<TModel>,
  Validator: FormValidator<TPartial>
) {
  const messageErrors = Validator.run(modelState) as { [key: string]: any }
  if (messageErrors) {
    let errors = (MessageErrors || {}) as { [key: string]: any }
    const keys = Object.keys(modelState as any)
      .filter((key) => !!(modelState as any)[key])
      .filter((key) => ((modelState as any)[key] instanceof File ? !!((modelState as any)[key] as File).size : false))
    keys.push(key as string)
    keys.forEach((key) => {
      if (messageErrors[key]) {
        errors[key] = messageErrors[key]
      } else {
        delete errors[key]
      }
    })
    return errors
  }
  return null
}

export const getErrorFromResponse = function <TModel>(error: AxiosError, modelRender: TModel) {
  const data = error.response?.data as any
  const finalData = data?.errors || data
  if (finalData) {
    const keys = Object.keys(modelRender as any)
    const MessageErrors: IPartialError<TModel> | undefined = {}
    keys.forEach((key) => {
      const messages = finalData[key]
      if (Array.isArray(messages) && messages.length > 0) {
        ;(MessageErrors as any)[key] = [{ message: messages[0] }]
      }
    })
    return MessageErrors
  }
}

export const getErrorCommonFromResponse = function <TModel>(error: AxiosError): string[] {
  const data = error.response?.data as any
  const finalData = data?.errors || data
  if (finalData && finalData['']) {
    const messages = finalData['']
    if (Array.isArray(messages)) {
      return messages.filter((msg) => msg && typeof msg === 'string' && msg.trim() !== '')
    }
  }
  return []
}

export const clearFieldEmpty = <TModel>(model: Extract<TModel, IModelBase>) => {
  Object.keys(model).forEach((key: keyof Extract<TModel, IModelBase>) => {
    if (!model[key]) delete model[key]
  })
}

export const validateMerge = <TModel>(...validateor: (FormValidator<TModel> | undefined)[]): FormValidator<TModel> => {
  const configs = validateor.map((x) => x?.configs).filter((x) => !!x)

  let temp: any = Object.assign({}, ...configs)

  for (let index = 0; index < Object.keys(temp).length; index++) {
    const key = Object.keys(temp)[index]
    //merge configs
    temp[key] = deepMerge({}, ...configs.map((x) => (x ? x[key] : { Rules: [] })))
    //merge rules
    temp[key].Rules = configs
      .map((x) => (x ? x[key] : { Rules: [] }))
      .reduce<IRuleOption<TModel>[]>((a, b) => {
        a.push(...(b?.Rules ?? []))
        return a
      }, [])
  }
  return new FormValidator(temp)
}

/**
 * Interface representing the result of a CSV validation.
 */
export interface IValidateCsv {
  /** Indicates whether the CSV data is valid */
  valid: boolean
  /** Optional message providing error details if validation fails */
  message?: string
}

/**
 * Validates a parsed CSV data array against a list of required column keys.
 *
 * @param data - An array of objects representing rows parsed from a CSV file.
 * @param requiredKeys - An array of keys that must exist in the CSV headers and each row.
 * @returns An object indicating whether the data is valid and an optional error message.
 */
export const validateCsvModel = <T extends Record<string, any>>(data: T[], requiredKeys: (keyof T)[]): IValidateCsv => {
  // If the data is empty, consider the file invalid
  if (data.length === 0) {
    return { valid: false, message: 'File is empty or cannot be parsed' }
  }

  // Extract headers from the first row
  const headers = Object.keys(data[0])

  // Check for missing required columns
  const missing = requiredKeys.filter((key) => !headers.includes(key as string))
  if (missing.length > 0) {
    return { valid: false, message: `Missing required columns: ${missing.join(', ')}` }
  }

  // Validate that each required column in each row has a non-empty string value
  for (let i = 0; i < data.length; i++) {
    const row = data[i]
    for (const key of requiredKeys) {
      const value = row[key as string]
      if (typeof value !== 'string' || value.trim() === '') {
        return {
          valid: false,
          message: `Invalid or missing value for "${key.toString()}" at row ${i + 2}` // +2 accounts for header and 0-based index
        }
      }
    }
  }

  // All validations passed
  return { valid: true }
}
